import React, { FC, useMemo } from 'react';
import YourCoursesCategoryLabelIcon from 'src/assets/icons/YourCoursesCategoryLabelIcon';
import { Markdown } from 'src/common/components/Markdown';
import { useDispatch, useSelector } from 'react-redux';
import {
  courseListingSelectedCategoriesSelector,
  courseListingShowAllCategoriesSelector,
} from 'src/state/courseListing/courseListingSelectors';
import { courseListingActions } from 'src/state/courseListing/courseListingActions';
import PlusIcon from 'src/assets/icons/PlusIcon';
import { Button } from 'src/common/components/Button';
import styled from 'styled-components';
import { colors } from 'src/config/colors';
import { StyleProps } from 'src/lib/styleProps';
import {
  CourseListingFilter,
  CourseListingFilterCheckbox,
  CourseListingFilterItem,
  CourseListingFilterList,
  CourseListingFilterTitle,
} from 'src/public/components/course-listing/CourseListingFilter';
import {
  useCommonCourseCategories,
  usePartnerCourseCategories,
} from 'src/common/hooks/useCommonCourseCategories';
import { CourseListingFilterItemGroupHeader } from 'src/public/components/course-listing/CourseListingFilterItemGroupHeader';
import LightbulbIcon from 'src/assets/icons/LightbulbIcon';

const ShowAllCategories = styled(Button)`
  color: ${colors.blue05};

  &:not(:disabled):hover,
  &:not(:disabled):focus {
    text-decoration: none;

    svg {
      color: ${colors.blue};
    }
  }

  svg {
    color: ${colors.darkGrey};
    margin-right: 0.75rem;
  }
`;

export const CourseListingCategoryFilter: FC<StyleProps> = ({ className, style }) => {
  const dispatch = useDispatch();
  const selectedCategories = useSelector(courseListingSelectedCategoriesSelector);
  const showAllCategories = useSelector(courseListingShowAllCategoriesSelector);
  const { data: commonCategories } = useCommonCourseCategories();
  const { data: partnerCategories } = usePartnerCourseCategories();

  const visibleCommonCategories = useMemo(
    () =>
      showAllCategories
        ? commonCategories
        : commonCategories?.filter((category) => selectedCategories[category.id]),
    [showAllCategories, commonCategories],
  );

  const visiblePartnerCategories = useMemo(
    () =>
      showAllCategories
        ? partnerCategories
        : partnerCategories?.filter((category) => selectedCategories[category.id]),
    [showAllCategories, partnerCategories],
  );

  return (
    <CourseListingFilter className={className} style={style}>
      <CourseListingFilterTitle>
        <YourCoursesCategoryLabelIcon size={18} />
        Kategoria szkoleń
      </CourseListingFilterTitle>

      <CourseListingFilterList>
        {visibleCommonCategories?.map((category) => (
          <CourseListingFilterItem active={selectedCategories[category.id]} key={category.id}>
            <CourseListingFilterCheckbox
              label={<Markdown text={category.title} />}
              onChange={() => dispatch(courseListingActions.toggleCategory(category.id))}
              value={selectedCategories[category.id]}
            />
          </CourseListingFilterItem>
        ))}

        {visiblePartnerCategories?.length > 0 && (
          <>
            <CourseListingFilterItemGroupHeader>
              <LightbulbIcon size={18} />
              Programy partnerskie
            </CourseListingFilterItemGroupHeader>

            {visiblePartnerCategories?.map((category) => (
              <CourseListingFilterItem active={selectedCategories[category.id]} key={category.id}>
                <CourseListingFilterCheckbox
                  label={<Markdown text={category.title} />}
                  onChange={() => dispatch(courseListingActions.toggleCategory(category.id))}
                  value={selectedCategories[category.id]}
                />
              </CourseListingFilterItem>
            ))}
          </>
        )}

        {!showAllCategories && (
          <CourseListingFilterItem active>
            <ShowAllCategories
              decoration="default"
              kind="link"
              onClick={() => dispatch(courseListingActions.setShowAllCategories(true))}
            >
              <PlusIcon size={18} />
              Pozostałe kategorie
            </ShowAllCategories>
          </CourseListingFilterItem>
        )}
      </CourseListingFilterList>
    </CourseListingFilter>
  );
};
